import {
  Box,
  ButtonBase,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Domain } from '@3nickels/data-modules';
import { Spacer } from '../../../../components';
import { qrCode } from '../../../../assets/png';
import { Svgs } from '../../../../assets/svg';
import { useAccountOwnerOptions } from '../../../../hooks/useAccountOwnerOptions';
import { ManualAccountIcon } from '../../accounts/ManualAccountTypes';
import { Colors } from '../../../../themes';
import ContinueLinkingRow from './ContinueLinkingRow';
import ManualCategoryCard from './ManualCategoryCard';
import ModalStepWithBack from './ModalStepWithBack';
import ModalTitleRow from './ModalTitleRow';
import { ContinueLinkingItem, ManualMenuOption } from './Types';
import type { ManualCategoryCardConfig } from './utils';

const listDividerSx = { borderColor: Colors.primaryLightest, mx: '6px' };

type ManualSearchResult = {
  icon: React.ReactNode;
  id: string;
  label: string;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  title: string;
};

const ManualSearchEmptyState: React.FC<{
  onOtherAssetClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onOtherDebtClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({ onOtherAssetClick, onOtherDebtClick }) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={1.5}>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: '#fff',
          border: `1px solid ${Colors.primaryLightest}`,
          borderRadius: '7px',
          display: 'flex',
          justifyContent: 'center',
          px: 2,
          py: 2.5,
        }}>
        <Typography align='center' color='secondary' variant='p16'>
          {t('ManualSearchNoExactMatchesTryInstead')}
        </Typography>
      </Box>
      <Stack spacing={0}>
        <ManualSearchResultRow
          onClick={onOtherAssetClick}
          result={{
            icon: (
              <Svgs.DisplaySmallCustomInvestment
                className='svg-img'
                style={{ height: 32, width: 32 }}
              />
            ),
            id: 'empty-state:other-asset',
            label: t('OtherAsset'),
            onClick: onOtherAssetClick,
            title: t('PropertiesAndAssets'),
          }}
        />
        <Divider sx={listDividerSx} />
        <ManualSearchResultRow
          onClick={onOtherDebtClick}
          result={{
            icon: ManualAccountIcon('Debt'),
            id: 'empty-state:other-debt',
            label: t('OtherDebt'),
            onClick: onOtherDebtClick,
            title: t('Debt'),
          }}
        />
      </Stack>
    </Stack>
  );
};

const ManualSearchResultRow: React.FC<{
  result: ManualSearchResult;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({ result, onClick }) => {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        'alignItems': 'center',
        'borderRadius': '7px',
        'display': 'flex',
        'justifyContent': 'flex-start',
        'position': 'relative',
        'px': 1,
        'py': 1.5,
        'textAlign': 'left',
        'width': '100%',
        '&::before': {
          backgroundColor: 'transparent',
          borderRadius: '7px',
          bottom: -1,
          content: '""',
          left: 0,
          position: 'absolute',
          right: 0,
          top: -1,
          transition: 'background-color 120ms ease',
          zIndex: 0,
        },
        '& > *': {
          position: 'relative',
          zIndex: 1,
        },
        '&:hover': {
          zIndex: 1,
        },
        '&:hover::before': {
          backgroundColor: Colors.disabledLight,
        },
      }}>
      <Stack
        alignItems='center'
        direction='row'
        useFlexGap
        spacing={'5px'}
        sx={{ justifyContent: 'space-between', minWidth: 0, width: '100%' }}>
        <Box
          sx={{
            alignItems: 'center',
            color: Colors.primaryBase,
            display: 'flex',
            flexShrink: 0,
            minHeight: 30,
            marginRight: '5px',
          }}>
          {result.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography color='secondary' sx={{ pr: 1, wordBreak: 'break-word' }} variant='p16'>
            {result.label}
          </Typography>
          <Typography color='primary' variant='p12'>
            {result.title}
          </Typography>
        </Box>
        <Box aria-hidden='true' sx={{ display: 'flex', flexShrink: 0, ml: 1.5, p: '2px' }}>
          <Svgs.IconArrowRight style={{ height: '40px', width: '40px' }} />
        </Box>
      </Stack>
    </ButtonBase>
  );
};

const AppleStep: React.FC<{
  onBack: () => void;
  onClose: () => void;
}> = ({ onBack, onClose }) => {
  const { t } = useTranslation();

  return (
    <ModalStepWithBack
      onBack={onBack}
      onClose={onClose}
      subtitle={t('AppleAccountsOnlyLinkedOnIPhone')}
      title={
        <Box sx={{ alignItems: 'center', columnGap: '15px', display: 'flex' }}>
          <Typography color='primary' variant='p30Bold'>
            {t('ConnectAppleOniPhone')}
          </Typography>
          <Box
            alt={t('Apple')}
            component='img'
            src='/images/appleLogoBlack.svg'
            sx={{ height: 33, marginBottom: '5px' }}
          />
        </Box>
      }>
      <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
        <img
          src={qrCode}
          alt={t('QRCode')}
          style={{
            maxWidth: '100px',
            marginBottom: '-29px',
          }}
        />
      </Box>
      <Spacer height='sm' />
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Typography
          align='center'
          color='primary'
          sx={{ display: 'block', width: '40%' }}
          variant='p16'>
          {t('ScanQRCodeWithIPhoneContinue')}
        </Typography>
      </Box>
    </ModalStepWithBack>
  );
};

const OwnerStep: React.FC<{
  onBack: () => void;
  onClose: () => void;
  onOwnerSelect: (owner: string) => void;
  selectedOwner: string;
}> = ({ onBack, onClose, onOwnerSelect, selectedOwner }) => {
  const { t } = useTranslation();
  const ownerOptions = useAccountOwnerOptions();

  return (
    <ModalStepWithBack
      onBack={onBack}
      onClose={onClose}
      subtitle={t('ToGiveBestAdviceWhoOwnsAccount')}
      title={t('ChooseAccountOwner')}>
      <Box
        sx={{ display: 'flex', justifyContent: 'center', mt: '15px', mb: '100px', width: '100%' }}>
        <ToggleButtonGroup
          disabled={false}
          exclusive
          fullWidth
          onChange={(_event, value) => {
            if (value !== null) {
              onOwnerSelect(value);
            }
          }}
          sx={{
            'width': '75%',
            'border': `1px solid ${Colors.primaryLighter}`,
            'borderRadius': '4px',
            'outline': 'none',
            '& .MuiToggleButtonGroup-grouped': {
              'borderWidth': '0px',
              'margin': 0,
              'outline': 'none',
              '&.Mui-selected': {
                backgroundColor: Colors.primaryLighter,
              },
              '&.Mui-selected:hover': {
                backgroundColor: Colors.primaryLight,
              },
              '&:hover': {
                backgroundColor: Colors.disabledLight,
              },
            },
          }}
          value={selectedOwner}>
          {ownerOptions.map((option) => {
            return (
              <ToggleButton
                key={option.value}
                value={option.value}
                sx={{
                  columnGap: '5px',
                  fontWeight: selectedOwner === option.value ? 'bold' : 'normal',
                  justifyContent: 'center',
                  opacity: 1,
                  padding: '5px 10px',
                  width: `${100 / ownerOptions.length}%`,
                }}>
                <Typography
                  color='secondary'
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  variant='p16'>
                  {option.label}
                </Typography>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Box>
    </ModalStepWithBack>
  );
};

const ManualStep: React.FC<{
  closeManualMenu: () => void;
  handleManualMenuItemClick: (item: ManualMenuOption) => void | Promise<void>;
  manualCategoryCards: ManualCategoryCardConfig[];
  manualMenuAnchorEl: HTMLElement | null;
  manualMenuItems: ManualMenuOption[];
  otherMenuItems: ManualMenuOption[];
  onBack: () => void;
  onClose: () => void;
  openManualMenu: (event: React.MouseEvent<HTMLButtonElement>, items: ManualMenuOption[]) => void;
}> = ({
  closeManualMenu,
  handleManualMenuItemClick,
  manualCategoryCards,
  manualMenuAnchorEl,
  manualMenuItems,
  otherMenuItems,
  onBack,
  onClose,
  openManualMenu,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = React.useState('');
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  const otherAssetMenuItem = React.useMemo(
    () => otherMenuItems.find((item) => item.target === 'other-asset'),
    [otherMenuItems]
  );

  const otherDebtMenuItem = React.useMemo(
    () => otherMenuItems.find((item) => item.target === 'debt-other'),
    [otherMenuItems]
  );

  const manualSearchResults = React.useMemo<ManualSearchResult[]>(() => {
    return manualCategoryCards.flatMap((card) => {
      // Cash & Banking has no menuItems — hardcode the full account type list
      if (!card.menuItems) {
        const cashItems: { label: string; subtype: Domain.CashAccountTypeEnum }[] = [
          { label: t('CashManagement'), subtype: Domain.CashAccountTypeEnum.CASH_MANAGEMENT },
          { label: t('CD'), subtype: Domain.CashAccountTypeEnum.CD },
          { label: t('Checking'), subtype: Domain.CashAccountTypeEnum.CHECKING },
          { label: t('MoneyMarket'), subtype: Domain.CashAccountTypeEnum.MONEY_MARKET },
          { label: t('Prepaid'), subtype: Domain.CashAccountTypeEnum.PREPAID },
          { label: t('Savings'), subtype: Domain.CashAccountTypeEnum.SAVINGS },
        ];
        return cashItems.map(({ label, subtype }) => ({
          icon: card.icon,
          id: `${card.title}:${label}`,
          label,
          onClick: () => {
            void handleManualMenuItemClick({
              id: `cash-account:${subtype}`,
              label,
              target: 'cash-account',
              subtype,
            });
          },
          title: card.title,
        }));
      }

      // Retirement Plans — expand grouped items into the full individual plan type list
      if (card.menuItems.some((item) => item.target === 'retirement-savings-plan')) {
        const retirementResults: ManualSearchResult[] = Domain.retirementPlanTypes.map(
          (planType) => ({
            icon: card.icon,
            id: `${card.title}:${Domain.planTypeToAccountLabel[planType]}`,
            label: Domain.planTypeToAccountLabel[planType],
            onClick: () => {
              void handleManualMenuItemClick({
                id: `retirement:${planType}`,
                label: Domain.planTypeToAccountLabel[planType],
                target: 'retirement-savings-plan',
                subtype: String(planType),
              });
            },
            title: card.title,
          })
        );
        const nonRetirementSavingsResults: ManualSearchResult[] = card.menuItems
          .filter((item) => item.target !== 'retirement-savings-plan')
          .map((item) => ({
            icon: card.icon,
            id: `${card.title}:${item.id}`,
            label: item.label,
            onClick: () => {
              void handleManualMenuItemClick(item);
            },
            title: card.title,
          }));
        return [...retirementResults, ...nonRetirementSavingsResults];
      }

      return card.menuItems.flatMap((item) => {
        if (card.title === t('Other')) {
          return [];
        }

        if (item.target === 'other-asset') {
          return {
            icon: (
              <Svgs.DisplaySmallCustomInvestment
                className='svg-img'
                style={{ height: 32, width: 32 }}
              />
            ),
            id: item.target,
            label: item.label,
            onClick: () => {
              void handleManualMenuItemClick(item);
            },
            title: t('PropertiesAndAssets'),
          };
        }

        if (item.target === 'debt-other') {
          return {
            icon: ManualAccountIcon('Debt'),
            id: item.target,
            label: item.label,
            onClick: () => {
              void handleManualMenuItemClick(item);
            },
            title: t('Debt'),
          };
        }

        return {
          icon: card.icon,
          id: `${card.title}:${item.id}`,
          label: item.label,
          onClick: () => {
            void handleManualMenuItemClick(item);
          },
          title: card.title,
        };
      });
    });
  }, [handleManualMenuItemClick, manualCategoryCards, t]);

  const filteredManualCategoryCards = React.useMemo(() => {
    if (!normalizedSearchValue) {
      return manualCategoryCards;
    }

    return manualCategoryCards.filter((card) => {
      const searchableText = [
        card.title,
        ...card.previewItems,
        ...(card.menuItems?.map((item) => item.label) ?? []),
      ]
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedSearchValue);
    });
  }, [manualCategoryCards, normalizedSearchValue]);

  const filteredManualSearchResults = React.useMemo(() => {
    if (!normalizedSearchValue) {
      return manualSearchResults;
    }

    return manualSearchResults.filter((result) => {
      const searchableText = `${result.label} ${result.title}`.toLowerCase();
      return searchableText.includes(normalizedSearchValue);
    });
  }, [manualSearchResults, normalizedSearchValue]);

  return (
    <ModalStepWithBack onBack={onBack} onClose={onClose} title={t('ManuallyAddAnAccount')}>
      <TextField
        fullWidth
        onChange={(event) => setSearchValue(event.target.value)}
        placeholder={t('SearchAccountTypes')}
        size='small'
        value={searchValue}
        sx={{
          'mb': 2,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#f3f9fa',
            borderRadius: '7px',
          },
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <Search color='primary' sx={{ fontSize: '30px' }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  sx={{
                    padding: 0,
                    visibility: searchValue.length > 0 ? 'visible' : 'hidden',
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={() => setSearchValue('')}>
                  <Svgs.ActionSmallCloseDefault style={{ height: '20px', width: '20px' }} />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <Box sx={{ maxHeight: 430, overflowY: 'auto', pb: 0.5 }}>
        {normalizedSearchValue ? (
          filteredManualSearchResults.length > 0 ? (
            <Stack spacing={0}>
              {filteredManualSearchResults.map((result, index) => (
                <React.Fragment key={result.id}>
                  <ManualSearchResultRow onClick={result.onClick} result={result} />
                  {index !== filteredManualSearchResults.length - 1 ? (
                    <Divider sx={listDividerSx} />
                  ) : null}
                </React.Fragment>
              ))}
            </Stack>
          ) : (
            <ManualSearchEmptyState
              onOtherAssetClick={() => {
                if (!otherAssetMenuItem) {
                  return;
                }

                void handleManualMenuItemClick(otherAssetMenuItem);
              }}
              onOtherDebtClick={() => {
                if (!otherDebtMenuItem) {
                  return;
                }

                void handleManualMenuItemClick(otherDebtMenuItem);
              }}
            />
          )
        ) : (
          <Grid container columnSpacing={2} rowSpacing={1.75}>
            {filteredManualCategoryCards.map((card) => (
              <Grid key={card.title} size={{ xs: 12, md: 6 }}>
                <ManualCategoryCard
                  icon={card.icon}
                  onClick={(event) => {
                    if (card.menuItems) {
                      openManualMenu(event, card.menuItems);
                      return;
                    }

                    void card.onClick?.();
                  }}
                  opensMenu={Boolean(card.menuItems)}
                  previewItems={card.previewItems}
                  title={card.title}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      <Menu
        anchorEl={manualMenuAnchorEl}
        onClose={closeManualMenu}
        open={manualMenuAnchorEl !== null}
        slotProps={{
          list: {
            sx: {
              p: 0,
            },
          },
          paper: {
            sx: {
              borderRadius: '10px',
              boxShadow:
                '0 4px 7px 1px rgba(30, 41, 105, 0.2), 0 -4px 7px 1px rgba(30, 41, 105, 0.2)',
              minWidth: 256,
              mt: 1,
              overflow: 'hidden',
              py: 1.5,
            },
          },
        }}>
        {manualMenuItems.map((item) => (
          <MenuItem
            key={item.id}
            onClick={() => void handleManualMenuItemClick(item)}
            sx={{
              alignItems: 'center',
              minHeight: 34,
              px: 2.5,
              py: 0.9375,
            }}>
            <Typography color='primary' variant='p16'>
              {item.label}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </ModalStepWithBack>
  );
};

const BrowseStep: React.FC<{
  continueLinkingItems: ContinueLinkingItem[];
  embeddedInstitutionSearch: React.ReactNode;
  hideContinueLinkingSection?: boolean;
  onAppleClick: () => void;
  onClose: () => void;
  onContinueLinkingClick: (item: ContinueLinkingItem) => void | Promise<void>;
}> = ({
  continueLinkingItems,
  embeddedInstitutionSearch,
  hideContinueLinkingSection = false,
  onAppleClick,
  onClose,
  onContinueLinkingClick,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <ModalTitleRow onClose={onClose} title={t('FindYourInstitution')} />
      <Spacer height='xs' />
      <Stack spacing={2}>
        <Box sx={{ minHeight: 420, overflow: 'visible' }}>{embeddedInstitutionSearch}</Box>

        <Box>
          <Typography color='primary' sx={{ mb: 0.75 }} variant='p16Bold'>
            {t('New')}
          </Typography>
          <ButtonBase
            aria-label={t('Apple')}
            onClick={onAppleClick}
            sx={{
              'alignItems': 'center',
              'border': `1px solid ${Colors.primaryLightest}`,
              'borderRadius': '7px',
              'display': 'flex',
              'justifyContent': 'flex-start',
              'pl': 1.5,
              'pr': 1,
              'py': 1.25,
              'textAlign': 'left',
              'width': '100%',
              '&:hover': {
                backgroundColor: Colors.disabledLight,
              },
            }}>
            <Box
              aria-hidden='true'
              alt=''
              component='img'
              src='/images/appleLogoBlack.svg'
              sx={{ flexShrink: 0, height: 28, mr: 1.5 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography color='secondary' variant='p16'>
                {t('Apple')}
              </Typography>
              <Typography color='primary' variant='p12'>
                {'www.apple.com'}
              </Typography>
            </Box>
            <Box aria-hidden='true' sx={{ display: 'flex', flexShrink: 0, ml: 1.5 }}>
              <Svgs.IconArrowRight className='svg-img' style={{ height: 25, width: 25 }} />
            </Box>
          </ButtonBase>
        </Box>

        {!hideContinueLinkingSection && continueLinkingItems.length > 0 ? (
          <Box>
            <Typography color='primary' variant='p16Bold'>
              {t('ContinueLinking')}
            </Typography>
            <Spacer height='xxs' />
            <Box>
              {continueLinkingItems.map((item, index) => (
                <React.Fragment key={`${item.accessId}:${item.accountName}`}>
                  <ContinueLinkingRow item={item} onClick={onContinueLinkingClick} />
                  {index !== continueLinkingItems.length - 1 ? (
                    <Divider sx={listDividerSx} />
                  ) : null}
                </React.Fragment>
              ))}
            </Box>
          </Box>
        ) : null}
      </Stack>
    </>
  );
};

export { AppleStep, BrowseStep, ManualStep, OwnerStep };
